import React, { useState } from "react";
import { Star, Check, Loader2 } from "lucide-react";

/** Affiche et remplit un questionnaire Price (app + page publique). */
export default function FormRenderer({ form, onSubmit, submitting }) {
  const [answers, setAnswers] = useState({});
  const [missing, setMissing] = useState(null);
  const set = (id, v) => { setAnswers((a) => ({ ...a, [id]: v })); setMissing(null); };

  const submit = (e) => {
    e.preventDefault();
    const empty = (v) => v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length);
    const miss = form.questions.find((q) => q.required && empty(answers[q.id]));
    if (miss) { setMissing(miss.id); document.getElementById(`fq-${miss.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    onSubmit(answers);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="form-renderer">
      {form.questions.map((q, i) => (
        <div key={q.id} id={`fq-${q.id}`} className={"rounded-2xl bg-white p-4 street-border-thin " + (missing === q.id ? "ring-4 ring-primary/40" : "")} data-testid={`form-q-${q.id}`}>
          <div className="mb-3 font-[Outfit] text-[15px] font-black leading-snug">
            {i + 1}. {q.label}{q.required && <span className="text-primary"> *</span>}
          </div>
          <QuestionInput q={q} value={answers[q.id]} onChange={(v) => set(q.id, v)} />
          {missing === q.id && <div className="mt-2 text-[12px] font-bold text-primary">Cette question est obligatoire</div>}
        </div>
      ))}
      <button type="submit" disabled={submitting} className="street-press flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 font-[Outfit] text-[16px] font-black text-primary-foreground street-border street-shadow disabled:opacity-60" data-testid="form-submit">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" strokeWidth={3} />} Envoyer mes réponses
      </button>
    </form>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={"street-press rounded-xl px-3 py-2.5 text-left text-[14px] font-bold street-border-thin " + (active ? "bg-primary text-primary-foreground" : "bg-secondary/30")}>
      {children}
    </button>
  );
}

function QuestionInput({ q, value, onChange }) {
  if (q.type === "single" || q.type === "multi") {
    const multi = q.type === "multi";
    const cur = multi ? (value || []) : value;
    return (
      <div className="grid gap-2">
        {multi && <div className="-mt-1 text-[11px] font-bold text-foreground/50">Plusieurs réponses possibles</div>}
        {q.options.map((o, j) => (
          <Chip key={`${j}-${o}`} testid={`form-opt-${q.id}-${j}`} active={multi ? cur.includes(o) : cur === o}
            onClick={() => onChange(multi ? (cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]) : o)}>{o}</Chip>
        ))}
      </div>
    );
  }
  if (q.type === "yesno") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Chip active={value === true} onClick={() => onChange(true)} testid={`form-yes-${q.id}`}>Oui</Chip>
        <Chip active={value === false} onClick={() => onChange(false)} testid={`form-no-${q.id}`}>Non</Chip>
      </div>
    );
  }
  if (q.type === "rating") {
    return (
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} className="street-press" data-testid={`form-star-${q.id}-${n}`} aria-label={`${n} étoiles`}>
            <Star className={"h-9 w-9 " + ((value || 0) >= n ? "text-[#FFB800]" : "text-foreground/20")} fill="currentColor" strokeWidth={1.5} stroke="#000" />
          </button>
        ))}
      </div>
    );
  }
  return (
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} maxLength={2000} placeholder="Ta réponse…"
      className="w-full rounded-xl bg-secondary/30 px-3 py-2.5 text-[15px] street-border-thin" style={{ fontSize: 16 }} data-testid={`form-text-${q.id}`} />
  );
}
